import Box from "@mui/material/Box";
import { Chip, Stack, Typography } from "@mui/material";
import { CartPricingMode, SVariantDto } from "@/__generated__/sdk";
import { SellerOffer } from "../sellerOffers";

type PriceRole = "primary" | "sticky" | "cash" | "credit";

/**
 * Only the buy box carries `data-price`. Seller rows and the mobile
 * mirror use other attributes so a crawler does not mix cash, credit,
 * and the default price.
 */
function priceRole(layout: "default" | "dense" | "buybox" | "inline", sellerOffer?: SellerOffer | null): PriceRole {
  if (layout === "inline") {
    return sellerOffer?.mode === CartPricingMode.Credit ? "credit" : "cash";
  }
  if (layout === "dense") return "sticky";
  return "primary";
}

function payAttribute(role: PriceRole) {
  if (role === "cash") return "data-cash-price";
  if (role === "credit") return "data-credit-price";
  if (role === "primary") return "data-price";
  return null;
}

function listAttribute(role: PriceRole) {
  if (role === "cash") return "data-cash-list-price";
  if (role === "credit") return "data-credit-list-price";
  if (role === "primary") return "data-selling_price";
  return null;
}

type Props = {
  variant?: SVariantDto | null;
  /** buybox: large price under the toman-off pill. inline: one row for a seller card. */
  layout?: "default" | "dense" | "buybox" | "inline";
  /** Cash/credit seller row — when set its prices are shown instead of the legacy variant price. */
  sellerOffer?: SellerOffer | null;
};

export default function VariantPrice({ variant, layout = "default", sellerOffer }: Props) {
  const isAvailable = variant?.hasStock;

  const listPrice = sellerOffer ? sellerOffer.price : variant?.price ?? 0;
  const discountedPrice = sellerOffer ? sellerOffer.offerPrice : variant?.offer?.price ?? null;
  const discountPercentage = sellerOffer ? sellerOffer.percentage : variant?.offer?.percentage;
  const hasDiscount = sellerOffer ? discountedPrice != null : Boolean(variant?.offer);

  const isDense = layout === "dense";
  const isBuyBox = layout === "buybox";
  const isInline = layout === "inline";
  const payPrice = hasDiscount ? (discountedPrice ?? 0) : listPrice;
  const role = priceRole(layout, sellerOffer);
  const payName = payAttribute(role);
  const listName = listAttribute(role);
  const payAttrs = payName ? { [payName]: payPrice } : {};
  const listAttrs = hasDiscount && listName ? { [listName]: listPrice } : {};
  const roleAttrs = {
    "data-price-role": role,
    "data-currency": "IRT",
    ...(role === "primary" && sellerOffer ? { "data-price-mode": sellerOffer.mode } : {}),
  };

  const styles = {
    priceVariant: isDense || isInline ? "h6" : "h4",
    oldPriceVariant: isDense || isInline ? "body2" : "subtitle1",
    discountVariant: isDense ? "caption" : "body1",
    spacerHeight: isDense || isBuyBox || isInline ? 0 : { xs: 5, md: 28 },
    badgeHeight: isDense ? 20 : 24,
    spacing: isDense ? 0.5 : 1,
    stickyBottom: isDense ? "16px" : "48px",
  } as const;

  if (!isAvailable) {
    return null;
  }

  if (isInline && variant) {
    return (
      <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap gap={0.75} data-price-section {...roleAttrs} sx={{ minWidth: 0 }}>
        {hasDiscount ? (
          <Box
            sx={{
              bgcolor: "error.main",
              color: "#fff",
              borderRadius: 1,
              px: 0.6,
              height: 20,
              display: "inline-flex",
              alignItems: "center",
              fontSize: "0.7rem",
              fontWeight: 800,
              lineHeight: 1,
            }}
          >
            %{discountPercentage}
          </Box>
        ) : null}
        {hasDiscount ? (
          <Typography component="s" variant="body2" color="grey.500" {...listAttrs} sx={{ fontSize: "0.8rem" }}>
            {listPrice.toLocaleString()}
          </Typography>
        ) : null}
        <Typography component="span" fontWeight={800} sx={{ fontSize: { xs: "1rem", sm: "1.15rem" }, lineHeight: 1.2 }} {...payAttrs}>
          {payPrice.toLocaleString()}
        </Typography>
        <Typography component="span" variant="body2" color="grey.600" data-currency="IRT">
          تومان
        </Typography>
      </Stack>
    );
  }

  if (isBuyBox && variant) {
    return (
      <Stack direction="row" alignItems="baseline" flexWrap="wrap" useFlexGap gap={1} data-price-section {...roleAttrs}>
        {hasDiscount ? (
          <Typography component="s" variant="body1" color="grey.500" {...listAttrs}>
            {listPrice.toLocaleString()}
          </Typography>
        ) : null}
        <Typography component="span" fontWeight={800} sx={{ fontSize: { xs: "1.45rem", sm: "1.65rem" }, lineHeight: 1.15 }} {...payAttrs}>
          {payPrice.toLocaleString()}
        </Typography>
        <Typography component="span" sx={{ fontSize: "0.95rem", color: "grey.700", fontWeight: 600 }} data-currency="IRT">
          تومان
        </Typography>
      </Stack>
    );
  }

  return (
    <Box
      data-price-section
      {...roleAttrs}
      sx={{
        width: "100%",
        // Dense layout lives inside the fixed bottom bar — sticky would fight it on mobile
        ...(isDense || isBuyBox || isInline
          ? { position: "static" }
          : {
              position: "sticky",
              bottom: styles.stickyBottom,
              left: 0,
              right: 0,
            }),
      }}
    >
      {variant ? (
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="flex-start"
          data-price-container
        >
          {hasDiscount ? (
            <Stack alignItems="flex-start" data-price-discounted>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={styles.spacing}
              >
                <Typography
                  variant={styles.oldPriceVariant}
                  color="grey.600"
                  component="s"
                  {...listAttrs}
                >
                  {listPrice.toLocaleString()} تومان
                </Typography>

                <Box
                  sx={{
                    backgroundColor: "error.100",
                    borderRadius: 4,
                    height: styles.badgeHeight,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    color="white.main"
                    sx={{ mx: isDense ? 0.75 : 1 }}
                    variant={styles.discountVariant}
                    lineHeight={1}
                  >
                    % {discountPercentage}
                  </Typography>
                </Box>
              </Stack>

              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={styles.spacing}
              >
                <Typography variant={styles.priceVariant} sx={{fontWeight:'bold'}} {...payAttrs}>
                  {(discountedPrice ?? 0).toLocaleString()}
                </Typography>

                <Typography variant={styles.priceVariant} color="grey.500" data-currency="IRT">
                  تومان
                </Typography>
              </Stack>
            </Stack>
          ) : (
            <Stack>
              <Box sx={{ height: styles.spacerHeight }} />

              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                spacing={styles.spacing}
              >
                <Typography
                  variant={styles.priceVariant}
                  component="div"
                  noWrap
                  {...payAttrs}
                >
                  {listPrice.toLocaleString()}
                </Typography>

                <Typography
                  variant={styles.priceVariant}
                  component="div"
                  color="grey.500"
                  data-currency="IRT"
                  noWrap
                >
                  تومان
                </Typography>
              </Stack>
            </Stack>
          )}
        </Stack>
      ) : (
        <Chip
          size={isDense ? "small" : "medium"}
          sx={{ backgroundColor: "grey.100" }}
          label={
            <Typography variant={isDense ? "caption" : "body2"} color="grey.500">
              ناموجود
            </Typography>
          }
        />
      )}
    </Box>
  );
}
